import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Search, CheckCircle2, XCircle, Clock, Wrench, Package, Truck, ThumbsUp, ThumbsDown } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import PageHero from "../components/PageHero";

const STATUS_STEPS = [
  { key: "received",          label: "Enquiry Received",   icon: Package },
  { key: "item_received",     label: "Device Received",    icon: Package },
  { key: "diagnosed",         label: "Diagnosed",          icon: Search },
  { key: "awaiting_approval", label: "Awaiting Approval",  icon: Clock },
  { key: "in_repair",         label: "In Repair",          icon: Wrench },
  { key: "completed",         label: "Repaired & Shipped", icon: Truck },
];

const STATUS_ORDER = STATUS_STEPS.map((s) => s.key);

function fmt(n) {
  return "₹" + Math.round(n).toLocaleString("en-IN");
}

function Timeline({ status }) {
  const currentIdx = STATUS_ORDER.indexOf(status);
  const isClosed = status === "closed";
  return (
    <div className="relative">
      {STATUS_STEPS.map((step, i) => {
        const done = !isClosed && i < currentIdx;
        const active = !isClosed && i === currentIdx;
        return (
          <div key={step.key} className="flex items-start gap-4 pb-6 last:pb-0">
            {/* Line */}
            <div className="flex flex-col items-center shrink-0">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all ${
                done ? "bg-gold border-gold" : active ? "border-gold bg-gold/10" : "border-slate-700 bg-navy-800"
              }`}>
                {done
                  ? <CheckCircle2 className="w-4 h-4 text-navy-900" />
                  : <step.icon className={`w-3.5 h-3.5 ${active ? "text-gold" : "text-slate-600"}`} />
                }
              </div>
              {i < STATUS_STEPS.length - 1 && (
                <div className={`w-0.5 h-6 mt-1 ${done ? "bg-gold/60" : "bg-slate-700"}`} />
              )}
            </div>
            <div className="pt-1">
              <div className={`text-sm font-bold ${done ? "text-gold" : active ? "text-white" : "text-slate-600"}`}>
                {step.label}
              </div>
              {active && <div className="text-[11px] text-gold/70 mt-0.5">Current stage</div>}
            </div>
          </div>
        );
      })}
      {isClosed && (
        <div className="flex items-center gap-3 mt-2">
          <XCircle className="w-8 h-8 text-rose-500 shrink-0" />
          <div className="text-sm font-bold text-rose-400">Enquiry Closed</div>
        </div>
      )}
    </div>
  );
}

export default function RepairTrackingPage() {
  const location = useLocation();
  const [query, setQuery] = useState("");
  const [enquiry, setEnquiry] = useState(null);
  const [loading, setLoading] = useState(false);
  const [approving, setApproving] = useState(false);
  const [approved, setApproved] = useState(null);

  // Pre-fill ref from ?ref= query param and auto-search
  useEffect(() => {
    const ref = new URLSearchParams(location.search).get("ref");
    if (ref) {
      setQuery(ref.toUpperCase());
      autoSearch(ref.toUpperCase());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function autoSearch(ref) {
    setLoading(true);
    try {
      const { data } = await api.get(`/repair/enquiries/${ref}`);
      setEnquiry(data);
    } catch {
      // silently fail — user can type manually
    } finally {
      setLoading(false);
    }
  }

  async function search(e) {
    e.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setEnquiry(null);
    setApproved(null);
    try {
      const { data } = await api.get(`/repair/enquiries/${query.trim().toUpperCase()}`);
      setEnquiry(data);
    } catch {
      toast.error("Enquiry not found. Please check the reference number.");
    } finally {
      setLoading(false);
    }
  }

  async function handleApproval(accept) {
    setApproving(true);
    try {
      await api.post(`/repair/enquiries/${enquiry.enquiry_number}/approve`, { approved: accept });
      setApproved(accept);
      setEnquiry((e) => ({
        ...e,
        status: accept ? "in_repair" : "closed",
        customer_approved: accept,
      }));
      toast.success(accept ? "Quote approved — we'll begin the repair!" : "Quote declined. We'll be in touch.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setApproving(false);
    }
  }

  const showApproval =
    enquiry?.status === "awaiting_approval" && enquiry?.customer_approved == null && approved === null;

  return (
    <div>
      <Helmet>
        <title>Track Repair — GamesTrade</title>
      </Helmet>

      <PageHero
        eyebrow="Service"
        title={<>Track your <span className="text-gold">repair</span></>}
        breadcrumbs={[{ to: "/", label: "Home" }, { to: "/repairs", label: "Repairs" }, { label: "Track" }]}
        size="compact"
      />
      <div className="site-container py-12">

      <form onSubmit={search} className="flex gap-3 mb-10">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Enter enquiry number (e.g. REP250503ABCDEF)"
          className="flex-1 bg-navy-700 border border-gold/20 rounded-lg px-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-gold/60"
        />
        <button
          type="submit"
          disabled={loading}
          className="bg-gold text-navy-900 font-extrabold uppercase tracking-wider text-sm px-6 py-3 rounded-lg hover:bg-gold-light transition disabled:opacity-50"
        >
          {loading ? "…" : <Search className="w-4 h-4" />}
        </button>
      </form>

      {enquiry && (
        <div className="space-y-6">
          {/* Header */}
          <div className="bg-navy-700 border border-gold/20 rounded-2xl p-6">
            <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-gold">{enquiry.enquiry_number}</div>
                <div className="text-xl font-black mt-1">{enquiry.device_name}</div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {enquiry.issues?.map((i) => (
                    <span key={i.id} className="text-[10px] font-bold uppercase tracking-wider bg-gold/10 text-gold border border-gold/20 rounded-full px-2 py-0.5">{i.label}</span>
                  ))}
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] text-slate-500 uppercase tracking-wider">Method</div>
                <div className="text-sm font-bold text-slate-300 mt-0.5">
                  {enquiry.pickup_required ? "Pickup" : "Drop-off"}
                </div>
              </div>
            </div>

            {/* Quote / cost section */}
            {enquiry.final_cost != null && (
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl px-5 py-4 mt-2">
                <div className="text-xs text-emerald-400 font-bold uppercase tracking-wider mb-1">Final Cost</div>
                <div className="text-2xl font-black text-white">{fmt(enquiry.final_cost)}</div>
                <div className="text-[11px] text-slate-400 mt-1">COD — payment collected on delivery / collection</div>
              </div>
            )}
            {enquiry.final_cost == null && enquiry.estimated_cost != null && (
              <div className="bg-gold/10 border border-gold/20 rounded-xl px-5 py-4 mt-2">
                <div className="text-xs text-gold font-bold uppercase tracking-wider mb-1">Quoted Price</div>
                <div className="text-2xl font-black text-white">{fmt(enquiry.estimated_cost)}</div>
                {enquiry.pickup_discount > 0 && (
                  <div className="text-[11px] text-emerald-400 mt-1">Includes pickup discount of −{fmt(enquiry.pickup_discount)}</div>
                )}
              </div>
            )}

            {/* Admin notes */}
            {enquiry.admin_notes && (
              <div className="bg-navy-800 border border-gold/10 rounded-xl px-4 py-3 mt-4 text-sm text-slate-300">
                <span className="text-gold font-bold text-xs uppercase tracking-wider">Update from GamesTrade: </span>
                {enquiry.admin_notes}
              </div>
            )}
          </div>

          {/* Approval action */}
          {showApproval && (
            <div className="bg-purple-500/10 border border-purple-500/30 rounded-2xl p-6">
              <div className="text-xs font-bold uppercase tracking-wider text-purple-300 mb-1">Action Required</div>
              <h3 className="text-lg font-black text-white mb-1">Approve the Repair Quote?</h3>
              <p className="text-slate-400 text-sm mb-2">
                Our team has diagnosed your <strong className="text-white">{enquiry.device_name}</strong> and quoted{" "}
                <strong className="text-gold">{fmt(enquiry.estimated_cost)}</strong>.
              </p>
              <p className="text-slate-500 text-xs mb-5">
                If you approve, we'll proceed with the repair and ship it back COD. If you decline, we'll return your device.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => handleApproval(true)}
                  disabled={approving}
                  className="flex items-center gap-2 bg-emerald-500 text-white font-extrabold uppercase tracking-wider text-xs px-6 py-3 rounded-full hover:bg-emerald-400 transition disabled:opacity-50"
                >
                  <ThumbsUp className="w-4 h-4" /> {approving ? "…" : "Approve & Proceed"}
                </button>
                <button
                  onClick={() => handleApproval(false)}
                  disabled={approving}
                  className="flex items-center gap-2 border border-rose-500/40 text-rose-400 font-bold uppercase tracking-wider text-xs px-6 py-3 rounded-full hover:bg-rose-500/10 transition disabled:opacity-50"
                >
                  <ThumbsDown className="w-4 h-4" /> Decline
                </button>
              </div>
            </div>
          )}

          {/* Post-approval confirmation */}
          {approved === true && (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-5 flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6 text-emerald-400 shrink-0" />
              <div>
                <div className="font-bold text-emerald-400">Repair Approved!</div>
                <div className="text-xs text-slate-400 mt-0.5">We'll get started and keep you updated. Payment (COD) is collected when the device is shipped back.</div>
              </div>
            </div>
          )}
          {approved === false && (
            <div className="bg-rose-500/10 border border-rose-500/30 rounded-2xl p-5 flex items-center gap-3">
              <XCircle className="w-6 h-6 text-rose-400 shrink-0" />
              <div>
                <div className="font-bold text-rose-400">Quote Declined</div>
                <div className="text-xs text-slate-400 mt-0.5">We'll contact you to arrange return of your device. No charges apply.</div>
              </div>
            </div>
          )}

          {/* Timeline */}
          <div className="bg-navy-700 border border-gold/20 rounded-2xl p-6">
            <div className="text-xs font-bold uppercase tracking-wider text-gold mb-6">Repair Progress</div>
            <Timeline status={enquiry.status} />
          </div>

          <p className="text-[11px] text-slate-600 text-center">
            Questions? WhatsApp us at +91 82877 99290 with your reference number.
          </p>
        </div>
      )}
      </div>
    </div>
  );
}
